import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, Briefcase, CheckSquare, Clock, FileText,
  Calendar, Shield, Receipt, DollarSign, StickyNote,
  BarChart3, ChevronLeft, ChevronRight, Bell, LogOut,
  Scale, User, Menu, Plus, ChevronDown, BookOpen, Settings,
} from 'lucide-react';

const CREATE_ITEMS = [
  { label: 'New Client',    icon: Users,       path: '/clients' },
  { label: 'New Case',      icon: Briefcase,   path: '/cases' },
  { label: 'New Task',      icon: CheckSquare, path: '/tasks' },
  { label: 'Log Time',      icon: Clock,       path: '/time-entries' },
  { label: 'New Invoice',   icon: Receipt,     path: '/invoices' },
  { label: 'New Expense',   icon: DollarSign,  path: '/expenses' },
  { label: 'Add Note',      icon: StickyNote,  path: '/notes' },
  { label: 'Upload Document', icon: FileText,  path: '/documents' },
  { label: 'Calendar Event', icon: Calendar,   path: '/calendar' },
];

function CreateNewDropdown() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors shadow-sm"
      >
        <Plus size={15} /> Create new
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <div className="py-1">
            {CREATE_ITEMS.map(({ label, icon: Icon, path }) => (
              <button
                key={label}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                onClick={() => { navigate(path); setOpen(false); }}
              >
                <Icon size={15} className="text-gray-400" />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ALL_NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: null },
  { to: '/clients', icon: Users, label: 'Clients', roles: null },
  { to: '/cases', icon: Briefcase, label: 'Cases', roles: null },
  { to: '/tasks', icon: CheckSquare, label: 'Tasks', roles: null },
  { to: '/time-entries', icon: Clock, label: 'Time Tracking', roles: null },
  { to: '/documents', icon: FileText, label: 'Documents', roles: null },
  { to: '/calendar', icon: Calendar, label: 'Calendar', roles: null },
  { to: '/trust', icon: Shield, label: 'Trust Accounts', roles: null },
  { to: '/invoices', icon: FileText, label: 'Billing', roles: null },
  { to: '/expenses', icon: DollarSign, label: 'Expenses', roles: null },
  { to: '/payments', icon: Receipt, label: 'Payments', roles: null },
  { to: '/notes', icon: StickyNote, label: 'Notes', roles: null },
  { to: '/employees', icon: User, label: 'Staff', roles: null },
  { to: '/templates', icon: FileText, label: 'Templates', roles: null },
  { to: '/accounting', icon: BookOpen, label: 'Accounting', roles: ['Admin', 'Accountant', 'Senior Partner', 'Partner'] },
  { to: '/reports', icon: BarChart3, label: 'Reports', roles: null },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['Admin', 'Senior Partner', 'Partner'] },
];

// Pages the Accountant role can access
const ACCOUNTANT_PATHS = new Set(['/', '/trust', '/invoices', '/expenses', '/payments', '/accounting', '/reports']);

// Defined OUTSIDE Layout so React sees a stable reference — prevents remount on every render.
function SidebarContent({ collapsed, user, onNavClick, onLogout, navItems }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-blue-800">
        <div className="w-9 h-9 bg-blue-400 rounded-lg flex items-center justify-center flex-shrink-0">
          <Scale size={20} className="text-white" />
        </div>
        {!collapsed && (
          <div>
            <div className="text-white font-bold text-sm leading-tight">Mutatu and Partners</div>
            <div className="text-blue-300 text-xs">Kwekwe, Zimbabwe</div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-colors text-sm font-medium
               ${isActive ? 'bg-blue-600 text-white' : 'text-blue-200 hover:bg-blue-800 hover:text-white'}`
            }
            onClick={onNavClick}
          >
            <Icon size={18} className="flex-shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-blue-800 p-3">
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{user?.first_name} {user?.last_name}</div>
              <div className="text-blue-300 text-xs truncate">{user?.role}</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={onLogout}
            className="mt-3 w-full flex items-center gap-2 text-blue-300 hover:text-white text-sm px-2 py-1.5 rounded hover:bg-blue-800 transition-colors"
          >
            <LogOut size={15} />
            Sign Out
          </button>
        )}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items by role: Accountant only sees financial pages;
  // items with a `roles` array are restricted to those roles only.
  const navItems = useMemo(() => {
    const role = user?.role || '';
    if (role === 'Accountant') {
      return ALL_NAV_ITEMS.filter(item => ACCOUNTANT_PATHS.has(item.to));
    }
    return ALL_NAV_ITEMS.filter(item => !item.roles || item.roles.includes(role));
  }, [user?.role]);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col relative bg-primary-800 transition-all duration-300 flex-shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ backgroundColor: '#1e3a5f' }}
      >
        <SidebarContent collapsed={collapsed} user={user} onNavClick={() => setMobileOpen(false)} onLogout={handleLogout} navItems={navItems} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white text-blue-900 rounded-full p-1 shadow-md z-20 border border-gray-200"
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-50 w-64 flex flex-col h-full" style={{ backgroundColor: '#1e3a5f' }}>
            <SidebarContent collapsed={collapsed} user={user} onNavClick={() => setMobileOpen(false)} onLogout={handleLogout} navItems={navItems} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </button>
          <div className="lg:hidden font-semibold text-gray-800">Mutatu and Partners</div>
          <div className="flex items-center gap-3 ml-auto">
            <CreateNewDropdown />
            <button className="relative text-gray-400 hover:text-gray-600">
              <Bell size={20} />
            </button>
            <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
